import { z } from 'zod';

function optionalDateSchema(invalidMessage) {
  return z.preprocess(
    val => (val === null || val === '' ? undefined : val),
    z
      .date({ invalid_type_error: invalidMessage })
      .optional()
      .transform(val => (val ? val.toISOString() : undefined)),
  );
}

const FK_ID_FIELDS = [
  'idPerfil',
  'idGenero',
  'idNacionalidade',
  'idRacaCor',
  'idEstado',
  'idEstadoNatal',
];

export const schema = z.object({
  // Vínculo com o Perfil de Usuário do Portal (obrigatório: 1 ficha por Perfil)
  idPerfil: z.string().min(1, 'Selecione o Perfil do usuário vinculado'),

  // 1. Dados Pessoais
  idGenero: z.string().optional(),
  idNacionalidade: z.string().optional(),
  cidadeNatal: z.string().optional(),
  idEstadoNatal: z.string().optional(),
  idRacaCor: z.string().optional(),

  // 2. Documentação
  rg: z.string().optional(),
  orgaoEmissor: z.string().optional(),
  ufRg: z.string().optional(),
  pisPasep: z.string().optional(),
  ctps: z.string().optional(),
  serieCtps: z.string().optional(),
  ufCtps: z.string().optional(),
  tituloEleitor: z.string().optional(),
  zonaEleitoral: z.string().optional(),
  secaoEleitoral: z.string().optional(),
  certificadoReservista: z.string().optional(),
  cnh: z.string().optional(),
  categoriaCnh: z.string().optional(),
  validadeCnh: optionalDateSchema('Validade da CNH inválida'),

  // 3. Endereço e Filiação
  endereco: z.string().optional(),
  bairro: z.string().optional(),
  cep: z
    .string()
    .optional()
    .refine(value => !value || value.replace(/\D/g, '').length === 8, {
      message: 'CEP deve conter 8 dígitos',
    }),
  cidade: z.string().optional(),
  idEstado: z.string().optional(),
  nomeMae: z.string().optional(),
  nomePai: z.string().optional(),
  contatoEmergenciaNome: z.string().optional(),
  contatoEmergenciaTelefone: z.string().optional(),

  // 4. Dados Bancários
  banco: z.string().optional(),
  agencia: z.string().optional(),
  conta: z.string().optional(),
  tipoConta: z.string().optional(),
  chavePix: z.string().optional(),

  // 5. Histórico Profissional
  ultimaEmpresa: z.string().optional(),
  cargoAnterior: z.string().optional(),
  periodoPermanenciaInicio: optionalDateSchema('Data inicial inválida'),
  periodoPermanenciaFim: optionalDateSchema('Data final inválida'),
  dataAdmissao: z
    .date({
      required_error: 'Data de admissão é obrigatória',
      invalid_type_error: 'Data de admissão é obrigatória',
    })
    .transform(val => val.toISOString()),
  cargoAtual: z.string().min(1, 'Cargo atual é obrigatório'),
  departamento: z.string().min(1, 'Departamento é obrigatório'),
  cursosEspecializacoes: z.string().optional(),

  // 6. Atividades
  descricaoAtividades: z.string().optional(),

  // 7. Documentos
  documentos: z
    .array(
      z.object({
        url: z.string().url('URL deve ser válida').optional().or(z.literal('')),
      }),
    )
    .optional(),
});

export const emptyDefaultValues = {
  idPerfil: '',
  idGenero: '',
  idNacionalidade: '',
  cidadeNatal: '',
  idEstadoNatal: '',
  idRacaCor: '',
  rg: '',
  orgaoEmissor: '',
  ufRg: '',
  pisPasep: '',
  ctps: '',
  serieCtps: '',
  ufCtps: '',
  tituloEleitor: '',
  zonaEleitoral: '',
  secaoEleitoral: '',
  certificadoReservista: '',
  cnh: '',
  categoriaCnh: '',
  validadeCnh: undefined,
  endereco: '',
  bairro: '',
  cep: '',
  cidade: '',
  idEstado: '',
  nomeMae: '',
  nomePai: '',
  contatoEmergenciaNome: '',
  contatoEmergenciaTelefone: '',
  banco: '',
  agencia: '',
  conta: '',
  tipoConta: 'C',
  chavePix: '',
  ultimaEmpresa: '',
  cargoAnterior: '',
  periodoPermanenciaInicio: undefined,
  periodoPermanenciaFim: undefined,
  dataAdmissao: undefined,
  cargoAtual: '',
  departamento: '',
  cursosEspecializacoes: '',
  descricaoAtividades: '',
  documentos: [],
};

export function buildDefaultValuesFromFuncionario(funcionario) {
  if (!funcionario) return emptyDefaultValues;

  const fkDefaults = {};
  FK_ID_FIELDS.forEach(field => {
    fkDefaults[field] = funcionario[field] ? String(funcionario[field]) : '';
  });

  return {
    ...emptyDefaultValues,
    ...funcionario,
    ...fkDefaults,
    validadeCnh: funcionario.validadeCnh ? new Date(funcionario.validadeCnh) : undefined,
    periodoPermanenciaInicio: funcionario.periodoPermanenciaInicio
      ? new Date(funcionario.periodoPermanenciaInicio)
      : undefined,
    periodoPermanenciaFim: funcionario.periodoPermanenciaFim
      ? new Date(funcionario.periodoPermanenciaFim)
      : undefined,
    dataAdmissao: funcionario.dataAdmissao ? new Date(funcionario.dataAdmissao) : undefined,
    documentos: (funcionario.documentos || []).map(documento => ({ url: documento.url || '' })),
  };
}

/**
 * Converte os ids de FK (string, vindos dos selects) para número antes de enviar ao backend —
 * mesmo padrão usado pela GMUD (`idCliente: Number(data.idCliente)` em Gmud/CreateContent).
 */
export function buildFuncionarioPayload(data) {
  const payload = { ...data };

  FK_ID_FIELDS.forEach(field => {
    payload[field] = data[field] ? Number(data[field]) : null;
  });

  payload.documentos = (data.documentos || [])
    .map(documento => ({ url: (documento.url || '').trim() }))
    .filter(documento => documento.url !== '');

  return payload;
}
